import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Banknote, Ban, CheckCheck, Printer, Undo2 } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card, CardHeader } from '@/components/common/Card';
import { Modal } from '@/components/common/Modal';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { TextField } from '@/components/forms/Field';
import { PageHeader } from '@/components/layout/PageHeader';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { paymentsApi, refundsApi } from '@/services/api/endpoints/finance';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { ROUTES } from '@/app/router/routes';
import { PERMISSIONS } from '@/types/permissions';
import type { PaymentMethod } from '@/types/finance';
import { formatDate, formatDateTime, formatMoney, orDash } from '@/utils/format';
import { Money, PaymentStatusBadge } from './shared';

/** One payment: what arrived, what it settled, and what can still be done with it. */
export function PaymentDetailPage() {
  const { id = '' } = useParams();
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();

  const [voiding, setVoiding] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [refunding, setRefunding] = useState(false);
  const [refund, setRefund] = useState({ amount: '', reason: '', method: 'BANK_TRANSFER' as PaymentMethod });

  const query = useQuery({
    queryKey: queryKeys.payments.detail(id),
    queryFn: () => paymentsApi.get(id),
    enabled: id !== '',
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.payments.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.invoices.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.finance.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.refunds.all });
  };

  const allocate = useMutation({
    mutationFn: () => paymentsApi.allocate(id, { autoAllocate: true, allocations: [] }),
    onSuccess: (result) => {
      toast.success(
        result.settledInvoices.length > 0
          ? `Settled ${result.settledInvoices.join(', ')}`
          : 'Applied to outstanding bills',
      );
      for (const warning of result.warnings) toast.info(warning);
      refresh();
    },
    onError: (error) => handleApiError(error),
  });

  const clear = useMutation({
    mutationFn: () => paymentsApi.markCleared(id),
    onSuccess: () => { toast.success('Marked cleared'); refresh(); },
    onError: (error) => handleApiError(error),
  });

  const voidPayment = useMutation({
    mutationFn: () => paymentsApi.void(id, voidReason.trim()),
    onSuccess: () => {
      toast.success('Payment voided');
      setVoiding(false);
      setVoidReason('');
      refresh();
    },
    onError: (error) => { setVoiding(false); handleApiError(error); },
  });

  const requestRefund = useMutation({
    mutationFn: () =>
      refundsApi.request({
        paymentId: id,
        amount: Number(refund.amount),
        reason: refund.reason.trim(),
        refundMethod: refund.method,
      }),
    onSuccess: (created) => {
      toast.success(`${created.refundNumber} requested — it needs approval before any money moves`);
      setRefunding(false);
      setRefund({ amount: '', reason: '', method: 'BANK_TRANSFER' });
      refresh();
    },
    onError: (error) => handleApiError(error),
  });

  if (query.isError) {
    return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  }
  if (query.isPending) {
    return <LoadingState label="Loading the payment" />;
  }

  const payment = query.data;
  const currency = payment.currencyCode;

  return (
    <div>
      <Link to={ROUTES.payments}
            className="mb-3 inline-flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-ink">
        <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
        All payments
      </Link>

      <PageHeader
        title={payment.receiptNumber}
        description={`${formatMoney(payment.amount, currency)} from ${payment.payerName ?? orDash(payment.studentName)} on ${formatDate(payment.paymentDate)}`}
        icon={<Banknote className="h-5 w-5" aria-hidden="true" />}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <PaymentStatusBadge status={payment.status} />
            <Link to={ROUTES.receipt(payment.id)}>
              <Button size="sm" variant="secondary">
                <Printer className="h-3.5 w-3.5" aria-hidden="true" />
                Receipt
              </Button>
            </Link>
            {payment.status === 'PENDING' && can(PERMISSIONS.PAYMENT_RECORD) && (
              <Button size="sm" loading={clear.isPending} onClick={() => clear.mutate()}>
                <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
                Mark cleared
              </Button>
            )}
            {payment.allocatable && can(PERMISSIONS.PAYMENT_ALLOCATE) && (
              <Button size="sm" loading={allocate.isPending} onClick={() => allocate.mutate()}>
                Apply {formatMoney(payment.unallocatedAmount, currency)}
              </Button>
            )}
            {payment.refundableAmount > 0 && payment.status !== 'CANCELLED'
              && can(PERMISSIONS.REFUND_REQUEST) && (
              <Button size="sm" variant="secondary" onClick={() => setRefunding(true)}>
                <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                Refund
              </Button>
            )}
            {payment.status !== 'CANCELLED' && can(PERMISSIONS.PAYMENT_VOID) && (
              <Button size="sm" variant="secondary" onClick={() => setVoiding(true)}>
                <Ban className="h-3.5 w-3.5" aria-hidden="true" />
                Void
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Figure label="Received" value={formatMoney(payment.amount, currency)} />
        <Figure label="Applied to bills" value={formatMoney(payment.allocatedAmount, currency)} />
        <Figure label="Held on account" value={formatMoney(payment.unallocatedAmount, currency)}
                hint={payment.unallocatedAmount > 0 ? 'Not yet applied to any bill' : undefined} />
        <Figure label="Refunded" value={formatMoney(payment.refundedAmount, currency)}
                hint={`${formatMoney(payment.refundableAmount, currency)} still refundable`} />
      </div>

      <Card>
        <CardHeader
          title="What this settled"
          description="Reversed allocations are kept so the history of the money stays readable."
        />
        {payment.allocations.length === 0 ? (
          <EmptyState
            title="Not applied to any bill"
            description={
              payment.unallocatedAmount > 0
                ? 'The whole amount is held on account and can be applied at any time.'
                : 'This payment has no live allocations.'
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-2">
                  {['Invoice', 'Period', 'Due'].map((h) => (
                    <th key={h} scope="col"
                        className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-subtle">
                      {h}
                    </th>
                  ))}
                  {['Applied', 'Still owes'].map((h) => (
                    <th key={h} scope="col"
                        className="px-4 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wider text-subtle">
                      {h}
                    </th>
                  ))}
                  <th scope="col"
                      className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-subtle">
                    Applied at
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {payment.allocations.map((allocation) => (
                  <tr key={allocation.id}>
                    <td className="px-4 py-2.5">
                      <Link to={ROUTES.invoiceDetail(allocation.invoiceId)}
                            className="font-mono text-xs text-accent hover:underline">
                        {orDash(allocation.invoiceNumber)}
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-muted">{orDash(allocation.billingPeriod)}</td>
                    <td className="px-4 py-2.5 tabular-nums text-muted">
                      {allocation.invoiceDueDate === undefined ? '—' : formatDate(allocation.invoiceDueDate)}
                    </td>
                    <td className="px-4 py-2.5"><Money amount={allocation.amount} bold /></td>
                    <td className="px-4 py-2.5"><Money amount={allocation.invoiceOutstanding} /></td>
                    <td className="px-4 py-2.5 text-[11px] tabular-nums text-subtle">
                      {formatDateTime(allocation.allocatedAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Modal
        open={voiding}
        title={`Void ${payment.receiptNumber}?`}
        description="Reverses every allocation and puts the invoices back where they were. This says the money never arrived — to return money that did arrive, raise a refund instead."
        onClose={() => setVoiding(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setVoiding(false)}>Keep it</Button>
            <Button variant="danger" loading={voidPayment.isPending}
                    disabled={voidReason.trim() === ''} onClick={() => voidPayment.mutate()}>
              Void payment
            </Button>
          </>
        }
      >
        <TextField label="Reason" required
                   placeholder="Recorded against the wrong child"
                   hint="A payment that vanished without an explanation is the hardest thing to reconstruct months later."
                   value={voidReason}
                   onChange={(event) => setVoidReason(event.target.value)} />
      </Modal>

      <Modal
        open={refunding}
        title="Request a refund"
        description={`${formatMoney(payment.refundableAmount, currency)} of this payment can still be returned. Nothing moves until it is approved and processed.`}
        onClose={() => setRefunding(false)}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRefunding(false)}>Cancel</Button>
            <Button loading={requestRefund.isPending}
                    disabled={refund.amount === '' || refund.reason.trim() === ''}
                    onClick={() => requestRefund.mutate()}>
              Request refund
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <TextField label="Amount" type="number" step="0.01" min="0.01"
                     max={payment.refundableAmount} required value={refund.amount}
                     onChange={(event) => setRefund((r) => ({ ...r, amount: event.target.value }))}
                     hint="Counts refunds already requested against this payment, not only processed ones." />
          <TextField label="Reason" required placeholder="Overpaid in error"
                     value={refund.reason}
                     onChange={(event) => setRefund((r) => ({ ...r, reason: event.target.value }))} />
          <p className="text-xs text-muted">
            Money held on account is released first. Only if that is not enough are settled bills
            reopened, newest first.
          </p>
        </div>
      </Modal>
    </div>
  );
}

function Figure({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="border border-line bg-surface px-4 py-3.5">
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-subtle">
        {label}
      </span>
      <span className="mt-1 block text-[20px] leading-none tabular-nums text-ink">{value}</span>
      {hint !== undefined && <span className="mt-1.5 block text-[11.5px] text-muted">{hint}</span>}
    </div>
  );
}
